import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, ReceiptIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { CaseEntries } from '../../components/admin/case/CaseEntries';
import { caseEntryTotals } from '../../utils/caseEntries';
import { formatKr } from '../../utils/quote';
import { defaultInvoiceBasis, useAdminData } from '../../contexts/AdminDataContext';
import type { InvoiceBasis } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages, priceModels } from '../../types/admin';
import type { CaseStatus, PhotoStage, PriceModel } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase,
    invoiceForCase,
    createInvoiceFromCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  /** Valgte billeder der endnu ikke er uploadet — kan ses og fjernes først. */
  const [pending, setPending] = useState<{
    file: File;
    preview: string;
  }[]>([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [basisChoice, setBasisChoice] = useState<InvoiceBasis | null>(null);
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-884" data-mp="3ofaxc978dmgt" />;
  const customer = customerById(item.customerId);
  const invoice = invoiceForCase(item.id);
  const entryCount = item.entries?.length ?? 0;
  const registeredSubtotal = caseEntryTotals(item.entries ?? []).subtotal;
  const agreedAmount = Number(item.finalAmount || item.fixedPrice) || 0;
  // Valget vises kun, når der reelt er to grundlag at vælge imellem.
  const canChooseBasis = item.priceModel === 'Fast pris' && entryCount > 0;
  const invoiceBasis: InvoiceBasis = canChooseBasis ? basisChoice ?? defaultInvoiceBasis(item) : entryCount > 0 ? 'entries' : defaultInvoiceBasis(item);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-885" data-mp="2hu083qw436nu">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-886" data-mp="2hvb9jk2342bs">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-887" data-mp="13y8a48wxdzub" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-888" data-mp="z4nnfd0s8saf">
        <div data-id="element-889" data-mp="v5ogxnzekngv">
          <p className="text-sm text-ink-muted" data-id="element-890" data-mp="v54ze8do013d">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-891" data-mp="q6bn2ja46g4s">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-892" data-mp="1iu7qs7y3p8b2">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-893" data-mp="1dtthkmkrjsbg">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-894" data-mp="2gzd3h6yq41f">
          <StatusBadge status={item.status} data-id="element-895" data-mp="3tm8xmtc6tq3n" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-896" data-mp="3tlhdqfrydgt7">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-897" data-mp="251a4jhtj36x2" />
            Slet sag
          </button>
        </div>
      </header>

      {item.quoteId && <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-898" data-mp="290spuep9hk0f">
          <p className="text-[15px] text-ink" data-id="element-899" data-mp="201nrp9hm3sos">
            Oprettet fra tilbud: <span className="font-medium" data-id="element-900" data-mp="jo77d8ky2fce">{item.quoteNumber}</span>
          </p>
          <Link to={`/admin/tilbud/${item.quoteId}`} className={buttonClasses('secondary', 'sm')} data-id="element-901" data-mp="jqa1o4t4qumx">
            Åbn tilbud
          </Link>
        </section>}

      {/* Faktura: oprettes fra sagen, typisk når arbejdet er færdigt. */}
      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-902" data-mp="c57pj7d4svv5">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-903" data-mp="q479w2tm2oli">
          <div data-id="element-904" data-mp="q237jpbd1xnc">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-905" data-mp="l5ma24i9u6xl">
              {invoice ? 'Faktura allerede oprettet' : 'Faktura'}
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-906" data-mp="3tj3aycubhk4r">
              {invoice ? `${invoice.invoiceNumber} · ${formatPrice(String(Math.round(invoice.total)))} · ${invoice.status}` : invoiceBasis === 'agreed' ? `Fakturaen udfyldes med den aftalte faste pris${item.quoteNumber ? ` fra ${item.quoteNumber}` : ''}. Alt kan rettes bagefter.` : entryCount > 0 ? `Fakturaen udfyldes med ${entryCount} registrerede linjer fra forbruget. Alt kan rettes bagefter.` : item.status === 'Færdig' ? 'Arbejdet er færdigt. Opret fakturaen ud fra sagen — alt kan rettes bagefter.' : 'Du kan oprette en fakturakladde, når du er klar. Alt kopieret kan rettes bagefter.'}
            </p>
          </div>
          {invoice ? <Link to={`/admin/fakturaer/${invoice.id}`} className={buttonClasses('secondary', 'md')} data-id="element-907" data-mp="1ohmn2ejntbud">
              <ReceiptIcon className="h-4 w-4" aria-hidden="true" data-id="element-908" data-mp="qvp8utcbbso8" />
              Åbn faktura
            </Link> : <Button size="md" disabled={creatingInvoice} onClick={async () => {
          if (creatingInvoice) return;
          setCreatingInvoice(true);
          setInvoiceError('');
          const created = await createInvoiceFromCase(item, invoiceBasis);
          setCreatingInvoice(false);
          if (!created) {
            setInvoiceError('Fakturaen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/fakturaer/${created.id}`);
        }} data-id="element-909" data-mp="10rscy4qq61dt">
              {creatingInvoice ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-910" data-mp="3rwf0owbe1fw0" />
                  Opretter …
                </> : 'Opret faktura'}
            </Button>}
        </div>
        {!invoice && canChooseBasis && <div className="mt-4 flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Fakturér ud fra" data-id="element-911" data-mp="3czfpyzl8p6xb">
            <span className="mr-1 text-sm text-ink-muted" data-id="element-912" data-mp="391hy1p0346q8">Fakturér ud fra:</span>
            {([['agreed', `Aftalt fast pris · ${formatKr(agreedAmount)}`], ['entries', `Registreret forbrug · ${formatKr(registeredSubtotal)}`]] as [InvoiceBasis, string][]).map(([value, label]) => <button key={value} type="button" role="radio" aria-checked={invoiceBasis === value} onClick={() => setBasisChoice(value)} className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-150 ${invoiceBasis === value ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line text-ink-soft hover:border-ink/30'}`} data-id="element-913" data-mp="b4mvzh6mbbfk">
                {label}
              </button>)}
          </div>}
        {invoiceError && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-914" data-mp="3jjdqa1ekn8n1">
            {invoiceError}
          </p>}
      </section>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-915" data-mp="1csnzattk3xpg">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-916" data-mp="1hsk24pqipj3f">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-917" data-mp="1hpo2mwz0umcv" />
        </div>
        <div className="mt-6" data-id="element-918" data-mp="3784gyfpzwh1e">
          <StatusTimeline status={item.status} data-id="element-919" data-mp="3772w5jj6gg6h" />
        </div>
      </section>

      <div className="mt-6" data-id="element-920" data-mp="3kyzkdchq65xl">
        <CaseEntries caseItem={item} data-id="element-921" data-mp="3l2doap61en75" />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-922" data-mp="3g0fy1az7ys4p">
        <div className="space-y-5" data-id="element-923" data-mp="3uygp9rp5kfsi">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-924" data-mp="3uxfcnishnl8o">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-925" data-mp="3pz4030at6wmr">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-926" data-mp="20k7epi97utit">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-927" data-mp="1vnh0z1kvwa0k">
              <div data-id="element-928" data-mp="2m0xevbegszda">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-929" data-mp="2lx9noiwhsp1q">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-930" data-mp="2h2ce9avawdob">{item.category}</dd>
              </div>
              <div data-id="element-931" data-mp="2c397nlzo9ze8">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-932" data-mp="2723exo60ic4p">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-933" data-mp="274g68rxntyuf">{item.address || '—'}</dd>
              </div>
              <div data-id="element-934" data-mp="2zqr4hvxsnno2">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-935" data-mp="2zq7m6e48ex9i">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-936" data-mp="2uziuaxon3y9u">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-937" data-mp="2q0690hdf2z9d" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-938" data-mp="3r02cbdejoak3">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-939" data-mp="3v0m9ui315g6r">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-940" data-mp="3si59fipebbn">
              <label htmlFor="ny-note" className="sr-only" data-id="element-941" data-mp="hs00acmlektg">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-942" data-mp="mlcf5v1gzd90" />
              <Button type="submit" variant="dark" data-id="element-943" data-mp="6ubkmu1fxmag">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-944" data-mp="lj7pyjc44l7d">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-945" data-mp="grzfrl2ykvt5">
                  <div data-id="element-946" data-mp="14hbokjudj1ux">
                    <p className="text-xs text-ink-muted" data-id="element-947" data-mp="14fr27hi7qp85">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-948" data-mp="zgnx5x28gj06">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-949" data-mp="31a3q50lsm9yb">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-950" data-mp="1718bvuju1xig" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-951" data-mp="y1br440kdiso">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-952" data-mp="124q9h1bz6b9a">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-953" data-mp="o4fhtw2ucsn2">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-954" data-mp="t1ywuy7d59g2">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-955" data-mp="2igx0srgnd6lw">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-956" data-mp="2ifc3iu2y79kc">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-957" data-mp="neieyzm3rfkb">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-958" data-mp="3mtds8ydtknju">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-959" data-mp="1sdosn66ei8pu" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-960" data-mp="1xbgtipp0x566" />}
                  {uploading ? 'Uploader …' : 'Vælg billeder'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  input.value = '';
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  setUploadError(problems.join(' '));
                  // Billederne lægges i kø, så de kan ses og fjernes før upload.
                  setPending(current => [...current, ...accepted.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }))]);
                }} data-id="element-961" data-mp="229ic79ijf2h4" />
                </label>
              </div>
            </div>

            {pending.length > 0 && <div className="mt-5 rounded-xl border border-line bg-canvas p-4" data-id="element-962" data-mp="1f91dqgy37o6a">
                <p className="text-sm text-ink-soft" data-id="element-963" data-mp="1aazvtzceduwe">
                  {pending.length} {pending.length === 1 ? 'billede' : 'billeder'} klar til {stage}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-964" data-mp="1o86apiu039gc">
                  {pending.map((entry, index) => <li key={`${entry.file.name}-${index}`} className="relative" data-id="element-965" data-mp="2zp4wj1lxlkou" data-mpi={index}>
                      <img src={entry.preview} alt={entry.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-966" data-mp="2vo2u6bwpwsin" />
                      <button type="button" onClick={() => {
                  URL.revokeObjectURL(entry.preview);
                  setPending(current => current.filter((_, i) => i !== index));
                }} aria-label={`Fjern ${entry.file.name}`} className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-967" data-mp="386yj4l8iyjjo">
                        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-968" data-mp="3h73g01g3dzjl" />
                      </button>
                    </li>)}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-969" data-mp="8q8jr4tsvdeh">
                  <Button size="sm" disabled={uploading} onClick={async () => {
                if (uploading) return;
                setUploading(true);
                setUploadError('');
                const result = await addPhotos(item.id, pending.map(entry => entry.file), stage);
                setUploading(false);
                if (!result.ok) {
                  // Køen bevares, så uploaden kan forsøges igen.
                  setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                  return;
                }
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
              }} data-id="element-970" data-mp="djbj5tl9dh2l">
                    {uploading ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-971" data-mp="1352ssfagsknp" />
                        Uploader …
                      </> : uploadError ? 'Prøv upload igen' : `Upload til ${stage}`}
                  </Button>
                  <button type="button" disabled={uploading} onClick={() => {
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
                setUploadError('');
              }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-50" data-id="element-972" data-mp="12xih5ffup3in">
                    Ryd
                  </button>
                </div>
              </div>}

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-973" data-mp="3sin7wev04b54">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-974" data-mp="24g0gtcrqqof2">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-975" data-mp="2e6v0qahilbxc">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-976" data-mp="2e7wggs9h6mle">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-977" data-mp="2o3zryoq7vxl0">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-978" data-mp="2s6w2ds5lsndv">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-979" data-mp="2x5pk5wrxb0ju" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-980" data-mp="12t9eax31hofi">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-981" data-mp="1e69g9je1w26r" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-982" data-mp="14aola57eg26i">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-983" data-mp="3owhrdkj2bk64">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-984" data-mp="3kwzg7gh1mngh">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-985" data-mp="3meo29h4wtnye">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-986" data-mp="3rd7ntgflcxq8">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-987" data-mp="3cfyu4g2tfhvc">
              <li data-id="element-988" data-mp="3hea5iv8re0fo">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-989" data-mp="3hh4oeic4jd6z">{customer?.email || '—'}</li>
              <li data-id="element-990" data-mp="33mc3139jz2sh">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-991" data-mp="38f533du2tmy8">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-992" data-mp="38gg6vdniuupc">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-993" data-mp="2tnwaqwyshnhe" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-994" data-mp="2kpauj8gsikjs">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-995" data-mp="2oqearw2r2ilj" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-996" data-mp="262lun5imm5cv">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-997" data-mp="2a35vaj2err3r">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-998" data-mp="2p0eprln0dazp">Økonomi</h2>
            <SelectField id="sag-prisform" label="Prisform" className="mt-4" hint={item.priceModel === 'Fast pris' ? 'Faktureres som aftalt fast pris.' : 'Faktureres ud fra registreret forbrug.'} value={item.priceModel} onChange={event => updateCase(item.id, {
            priceModel: event.target.value as PriceModel
          })} options={priceModels.map(option => ({
            value: option,
            label: option
          }))} data-id="element-999" data-mp="2k2womhf8g029" />
            <dl className="mt-2 divide-y divide-line/70 text-[15px]" data-id="element-1000" data-mp="3mvinnt44ay3s">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1001" data-mp="3qwaygqsn1b26">
                <dt className="text-ink-soft" data-id="element-1002" data-mp="3quhszetrgwvr">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-1003" data-mp="3vmrdugg0r6r6">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1004" data-mp="4fevvz7bapa7">
                <dt className="text-ink-soft" data-id="element-1005" data-mp="33wd48wf1gglb">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-1006" data-mp="33xy1wuwngfne">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1007" data-mp="37z9r4zbm2yv2">
                <dt className="text-ink-soft" data-id="element-1008" data-mp="3cuyk2jt4lhie">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-1009" data-mp="3hw5tenax2or6">{formatPrice(item.estimate)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1010" data-mp="s73shrr61zwy">
                <dt className="text-ink-soft" data-id="element-1011" data-mp="x5f1k09n06e4">Registreret forbrug</dt>
                <dd className="font-medium text-ink" data-id="element-1012" data-mp="x3kg8kugec9i">
                  {formatKr(caseEntryTotals(item.entries ?? []).subtotal)}
                  <span className="ml-1 text-xs font-normal text-ink-muted" data-id="element-1013" data-mp="2cocy9986ardr">ekskl. moms</span>
                </dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-1014" data-mp="27wbq9zi1fmtj" />
          </section>
        </aside>
      </div>
    </div>;
}